/**
 * SSO landing page - Workstream B (2026-07-12).
 *
 * Where the SAML ACS / OIDC callback redirects the browser after the IdP
 * authenticates the user. Unlike the Core bridge (/auth/bridge), the token
 * here is ALREADY a full Discovery session JWT (the api-controller minted it
 * in samlAcsHandler / oidcCallbackHandler), so there is nothing to exchange -
 * we read it from the fragment, establish the session, and go.
 *
 *   https://<host>/sso/landing#access_token=<jwt>&expires_at=<iso>&return_url=/
 *
 * The token is HS256; its payload carries { tenantId, actorRole, email, sub }.
 * We decode the payload client-side ONLY to populate the local user card
 * (email / role / tenantId) - the signature is authoritative and re-verified
 * by the api-controller on every subsequent call, so a tampered payload buys
 * nothing (the API would reject the token). We never trust it for authz.
 *
 * The fragment is wiped immediately (replaceState) so the token never lands
 * in history / Referer / share sheets - same defence as Bridge.tsx.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Loader2, ShieldAlert } from 'lucide-react';

import { Mark } from '@/components/brand/Mark';
import { setAuth, type AuthenticatedUser } from '@/lib/api';

type Status =
  | { state: 'reading' }
  | { state: 'success' }
  | { state: 'error'; title: string; detail: string };

interface LandingFragment {
  accessToken: string | null;
  expiresAt: string | null;
  returnUrl: string;
}

function parseLandingFragment(hash: string): LandingFragment {
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!trimmed) return { accessToken: null, expiresAt: null, returnUrl: '/' };
  const params = new URLSearchParams(trimmed);
  const returnUrlRaw = params.get('return_url');
  // Open-redirect defence: relative path only, never an absolute URL.
  const returnUrl =
    returnUrlRaw && returnUrlRaw.startsWith('/') && !returnUrlRaw.startsWith('//')
      ? returnUrlRaw
      : '/';
  return {
    accessToken: params.get('access_token'),
    expiresAt: params.get('expires_at'),
    returnUrl,
  };
}

/**
 * Decode a JWT payload (base64url) WITHOUT verifying the signature. Used only
 * to render the local user object; the api-controller is the authority.
 */
function decodeJwtUser(token: string): AuthenticatedUser | null {
  try {
    const payloadB64 = token.split('.')[1];
    if (!payloadB64) return null;
    const json = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as {
      tenantId?: string;
      actorRole?: string;
      email?: string;
      sub?: string;
    };
    if (!claims.tenantId) return null;
    const email = claims.email ?? claims.sub ?? '';
    return {
      email,
      displayName: email || 'SSO user',
      role: claims.actorRole ?? 'compliance_analyst',
      tenantId: claims.tenantId,
    };
  } catch {
    return null;
  }
}

export function SsoLanding() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>({ state: 'reading' });

  useEffect(() => {
    const { accessToken, expiresAt, returnUrl } = parseLandingFragment(window.location.hash);

    // Wipe the fragment immediately - defence in depth so the token doesn't
    // survive in browser history or document.referrer.
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    if (!accessToken) {
      setStatus({
        state: 'error',
        title: 'Sign-in link incomplete',
        detail:
          'This sign-in didn’t carry its session token. It may have already been used, or your browser stripped it. Return to the login page and sign in with SSO again.',
      });
      return;
    }

    const user = decodeJwtUser(accessToken);
    if (!user) {
      setStatus({
        state: 'error',
        title: 'Could not read your session',
        detail:
          'The sign-on token from your identity provider was malformed. Return to the login page and try again, or ask your administrator to check the SSO configuration.',
      });
      return;
    }

    // No network round-trip: the token is a full Discovery session already.
    // Default a 8h expiry only if the callback didn't provide one.
    const expiry = expiresAt ?? new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString();
    setAuth(accessToken, user, expiry);
    setStatus({ state: 'success' });
    const t = setTimeout(() => {
      navigate({ to: returnUrl, replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-6">
          <Mark className="h-12 w-12" />

          {status.state === 'reading' ? (
            <div className="flex flex-col items-center gap-3 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
              <div>
                <div className="text-lg font-semibold">Signing you in to FOCTTA Discovery…</div>
                <div className="text-sm text-muted-foreground">
                  Completing single sign-on from your identity provider.
                </div>
              </div>
            </div>
          ) : null}

          {status.state === 'success' ? (
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">
                You’re in. Loading your dashboard…
              </div>
            </div>
          ) : null}

          {status.state === 'error' ? (
            <div className="flex flex-col items-center gap-3 text-center">
              <ShieldAlert className="h-10 w-10 text-amber-500" aria-hidden />
              <div>
                <div className="text-lg font-semibold">{status.title}</div>
                <div className="mt-2 text-sm text-muted-foreground">{status.detail}</div>
              </div>
              <button
                type="button"
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                onClick={() => navigate({ to: '/login' })}
              >
                Back to sign in
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default SsoLanding;
